// RITA-1602 — Bild im Handbuchtext. Es lädt erst, wenn es in den sichtbaren Bereich kommt, und reserviert vorher schon
// seinen Platz (Breite und Höhe sind bekannt): So verrutscht der Text nicht, und Sprünge landen an der richtigen Stelle.
// Ein Klick öffnet das Bild groß über der Seite, weil ein Bildschirmfoto in der schmalen Leiste nur grob zu lesen ist.
// Kleine Symbole („Knöpfe und Symbole im PRM“) stehen auf hellem Grund, weil sie die helle Ansicht zeigen, und werden
// nicht vergrößert.
import React, { useState } from 'react';
import { Box, ButtonBase, Dialog, DialogContent, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import { Bild, handbuchBildAdresse } from '../model/handbuchInhalt';

export const BILD_VERGROESSERN = 'Vergrößern';
export const BILD_SCHLIESSEN = 'Bild schließen';

interface Props {
  bild: Bild;
  unterschrift: React.ReactNode;
}

export default function HandbuchBild({ bild, unterschrift }: Props) {
  const [gross, setGross] = useState(false);
  const adresse = handbuchBildAdresse(bild.datei);

  if (bild.klein) {
    return (
      <Box
        component="span"
        sx={{ display: 'inline-block', bgcolor: 'common.white', borderRadius: 0.5, p: 0.25, mb: 0.5 }}
      >
        <img
          src={adresse}
          alt={bild.alt}
          loading="lazy"
          width={bild.klein}
          height={Math.round((bild.klein * bild.hoehe) / bild.breite)}
          data-testid="handbuch-symbol"
          style={{ display: 'block' }}
        />
      </Box>
    );
  }

  return (
    <Box component="figure" data-testid="handbuch-bild" sx={{ m: 0, my: 1.5 }}>
      <ButtonBase
        onClick={() => setGross(true)}
        aria-label={`${BILD_VERGROESSERN}: ${bild.alt}`}
        data-testid="handbuch-bild-knopf"
        sx={{
          display: 'block',
          position: 'relative',
          width: '100%',
          maxWidth: 680,
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden',
          bgcolor: 'common.white',
          cursor: 'zoom-in',
        }}
      >
        {/* Echtes img statt Box: Box würde width und height als Stil nehmen, hier sind es die Maße für den Platzhalter. */}
        <img
          src={adresse}
          alt={bild.alt}
          loading="lazy"
          width={bild.breite}
          height={bild.hoehe}
          style={{ display: 'block', width: '100%', height: 'auto' }}
        />
        <Box
          component="span"
          aria-hidden="true"
          sx={{
            position: 'absolute',
            right: 6,
            bottom: 6,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.25,
            px: 0.75,
            borderRadius: 2,
            bgcolor: 'rgba(22, 28, 36, 0.78)',
            color: 'common.white',
            fontSize: 12,
            lineHeight: '20px',
          }}
        >
          <ZoomInIcon sx={{ fontSize: 14 }} />
          {BILD_VERGROESSERN}
        </Box>
      </ButtonBase>
      {unterschrift ? (
        <Typography component="figcaption" variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {unterschrift}
        </Typography>
      ) : null}
      <Dialog
        open={gross}
        onClose={() => setGross(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ 'aria-label': bild.alt || 'Bild' }}
        data-testid="handbuch-bild-gross"
      >
        <IconButton
          aria-label={BILD_SCHLIESSEN}
          onClick={() => setGross(false)}
          size="small"
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            bgcolor: 'background.paper',
            '&:hover': { bgcolor: 'background.neutral' },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <DialogContent sx={{ p: 2, pt: 5 }}>
          <Box
            component="img"
            src={adresse}
            alt={bild.alt}
            sx={{ display: 'block', width: '100%', height: 'auto', bgcolor: 'common.white' }}
          />
          {unterschrift ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {unterschrift}
            </Typography>
          ) : null}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
